import React, { useMemo, useState } from 'react';
import { buildClientDisplayName } from '../serviceOptions';
import { resolveClosureStatusLabel } from '../serviceBilling';

const formatCurrency = (value = 0) =>
  new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

export default function ServiceClosuresView({
  closures = [],
  clients = [],
  services = [],
  payingClosureId = '',
  onMarkPaid,
}) {
  const [clientFilter, setClientFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('pending');

  const clientNameById = useMemo(() => {
    const map = new Map();
    clients.forEach((client) => {
      map.set(String(client?.id || '').trim(), buildClientDisplayName(client));
    });
    return map;
  }, [clients]);

  const filteredClosures = useMemo(() => {
    return closures
      .filter((closure) => {
        if (clientFilter !== 'all' && String(closure?.clientId || '').trim() !== clientFilter) return false;
        if (statusFilter === 'pending') return String(closure?.status || '').trim().toLowerCase() !== 'paid';
        if (statusFilter === 'paid') return String(closure?.status || '').trim().toLowerCase() === 'paid';
        return true;
      })
      .sort((a, b) => {
        const aTime = new Date(a?.recognizedAt || a?.paidAt || a?.updatedAt || 0).getTime();
        const bTime = new Date(b?.recognizedAt || b?.paidAt || b?.updatedAt || 0).getTime();
        return bTime - aTime;
      });
  }, [closures, clientFilter, statusFilter]);

  const serviceCount = filteredClosures.reduce((acc, closure) => acc + Number(closure?.tripCount || 0), 0);
  const totalPending = filteredClosures.reduce((acc, closure) => acc + Number(closure?.saleTotalPending || 0), 0);

  return (
    <div className="space-y-4">
      <div className="rounded-[30px] border border-slate-200 bg-[linear-gradient(135deg,#ffffff_0%,#f8fafc_100%)] px-5 py-5 shadow-[0_14px_34px_rgba(15,23,42,0.06)]">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">Cierres comerciales</p>
            <h3 className="mt-2 text-xl font-black tracking-tight text-slate-900">Semanal y mensual, listos para cobrar</h3>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-500">
              Acá juntamos los viajes acumulativos por cliente y período. Cuando marcás pago realizado, recién ahí impactan ventas y diferencia en balance general.
            </p>
          </div>
          <div className="grid min-w-[320px] grid-cols-2 gap-2">
            <div className="rounded-2xl border border-cyan-200 bg-cyan-50 px-3 py-2 shadow-sm">
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-cyan-700">Viajes acumulados</p>
              <p className="mt-1 text-lg font-black tracking-tight text-cyan-900">{serviceCount}</p>
            </div>
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-3 py-2 shadow-sm">
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-700">Total a cobrar</p>
              <p className="mt-1 text-lg font-black tracking-tight text-emerald-900">{formatCurrency(totalPending)}</p>
            </div>
          </div>
        </div>

        <div className="mt-4 grid gap-3 xl:grid-cols-[minmax(0,1fr)_220px_220px]">
          <label className="block space-y-2">
            <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Cliente</span>
            <select value={clientFilter} onChange={(event) => setClientFilter(event.target.value)} className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-cyan-300 focus:ring-4 focus:ring-cyan-100">
              <option value="all">Todos los clientes</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {buildClientDisplayName(client)}
                </option>
              ))}
            </select>
          </label>
          <label className="block space-y-2">
            <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Estado</span>
            <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-cyan-300 focus:ring-4 focus:ring-cyan-100">
              <option value="pending">Pendientes / listos</option>
              <option value="paid">Pagados</option>
              <option value="all">Todos</option>
            </select>
          </label>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600 shadow-sm">
            {filteredClosures.length} cierre{filteredClosures.length === 1 ? '' : 's'} visibles
          </div>
        </div>
      </div>

      {filteredClosures.length > 0 ? (
        <div className="space-y-3">
          {filteredClosures.map((closure) => {
            const normalizedStatus = String(closure?.status || '').trim().toLowerCase();
            const linkedServices = services.filter((service) =>
              Array.isArray(closure?.linkedServiceIds) && closure.linkedServiceIds.includes(service.id)
            );

            return (
              <div key={closure.id} className="rounded-[28px] border border-slate-200 bg-white px-5 py-5 shadow-[0_14px_34px_rgba(15,23,42,0.06)]">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.2em] ${
                        normalizedStatus === 'paid'
                          ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                          : 'border-amber-200 bg-amber-50 text-amber-700'
                      }`}>
                        {resolveClosureStatusLabel(closure?.status)}
                      </span>
                      <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-600">
                        {closure?.billingCadence === 'weekly' ? 'Semanal' : 'Mensual'}
                      </span>
                    </div>
                    <p className="mt-3 text-xl font-black tracking-tight text-slate-950">
                      {clientNameById.get(String(closure?.clientId || '').trim()) || closure?.clientNameSnapshot || 'Cliente'}
                    </p>
                    <p className="mt-1 text-sm text-slate-500">{closure?.periodLabel || 'Período acumulado'}</p>
                  </div>

                  <div className="grid min-w-[330px] grid-cols-2 gap-2 lg:grid-cols-4">
                    <Metric label="Viajes" value={closure?.tripCount || 0} />
                    <Metric label="Costo" value={formatCurrency(closure?.operationalCostTotal || 0)} />
                    <Metric label="Diferencia" value={formatCurrency(closure?.marginTotalPending || 0)} accent="cyan" />
                    <Metric label="Monto" value={formatCurrency(closure?.saleTotalPending || 0)} accent="emerald" />
                  </div>
                </div>

                {linkedServices.length > 0 ? (
                  <div className="mt-4 rounded-[22px] border border-slate-200 bg-slate-50 px-4 py-4">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Viajes incluidos</p>
                    <div className="mt-3 space-y-2">
                      {linkedServices.slice(0, 6).map((service) => (
                        <div key={service.id} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-white bg-white px-3 py-3 text-sm shadow-sm">
                          <div>
                            <p className="font-semibold text-slate-900">{service.origin || 'Origen'} → {service.destination || 'Destino'}</p>
                            <p className="mt-1 text-xs text-slate-500">{service.serviceTaskLabel || service.serviceModeLabel || 'Servicio'} · {service.serviceTransportLabel || service.serviceTypeLabel || 'Transporte'}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-slate-900">{formatCurrency(service?.realizedSalePrice || service?.salePrice || 0)}</p>
                            <p className="mt-1 text-xs text-slate-500">{formatCurrency(service?.realizedOperationalCost || service?.estimatedOperationalCost || 0)} costo</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}

                <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 pt-4">
                  <p className="text-xs text-slate-500">
                    {normalizedStatus === 'paid'
                      ? `Pagado ${closure?.paidAt ? new Date(closure.paidAt).toLocaleString('es-AR') : 'sin fecha visible'}`
                      : 'La liquidación de transporte ya impactó en gastos. Este cierre espera cobro para reconocer ventas y diferencia.'}
                  </p>
                  {normalizedStatus !== 'paid' ? (
                    <button
                      type="button"
                      onClick={() => onMarkPaid?.(closure)}
                      disabled={payingClosureId === closure.id}
                      className="inline-flex items-center gap-2 rounded-2xl border border-emerald-300 bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {payingClosureId === closure.id ? 'Registrando pago...' : 'Pago realizado'}
                    </button>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="rounded-[24px] border border-dashed border-slate-300 bg-slate-50/70 px-6 py-8 text-center">
          <p className="text-base font-bold text-slate-900">Todavía no hay cierres para este filtro</p>
          <p className="mt-2 text-sm leading-6 text-slate-500">
            Cuando existan viajes semanales o mensuales pendientes de cobro, los vamos a agrupar acá para revisarlos y marcarlos como pagados.
          </p>
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, accent = 'slate' }) {
  const palette = {
    slate: 'border-slate-200 bg-white text-slate-900',
    cyan: 'border-cyan-200 bg-cyan-50 text-cyan-900',
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-900',
  };

  return (
    <div className={`rounded-[18px] border px-3 py-2 shadow-sm ${palette[accent] || palette.slate}`}>
      <p className="text-[10px] font-semibold uppercase tracking-[0.18em] opacity-70">{label}</p>
      <p className="mt-1 text-sm font-black tracking-tight">{value}</p>
    </div>
  );
}
